/**
 * This file contains the mapping of sprite keys for each of the static spatial
 * layers currently supported in RSP.
 *
 * Why is this file needed?
 *
 * Andromeda allows for almost complete setup of static spatial layers except
 * for their marker images (icons) - so RSP currently ships with images that
 * correspond to each of the spatial layers.
 *
 * How should I update this file?
 *
 * If adding or changing an icon, first save it in the Map/assets folder
 * (preferably as an SVG) and then update Map/assets/sprites-manifest.json to
 * include the name of the file. This will ensure that the sprite is included in
 * the sprite generation process.
 *
 * How do I generate an updated sprite?
 *
 * Go to dev_scripts/sprites-gen and run: `yarn start`. sprites-gen will
 * read the Map/assets/sprites-manifest.json and generate the updated sprite
 * and sprites-info.json file.
 *
 * The sprites-info.json file is imported by StaticLayerGL to get the
 * coordinates of the sprite image.
 *
 * See dev_scripts/sprites-gen/README.md for more information.
 *
 * How does this file work?
 *
 * The sprite generation process generates a sprites-info.json file that
 * contains the coordinates of each of the sprites in the sprite image. This
 * file is imported here and used to map the sprite keys to the coordinates.
 *
 * Use it like this:
 *
 * import {
 *   spritesImage,
 *   spritesMapping,
 *   getStaticSpritesIcon
 * } from './getStaticSpritesIcon';
 *
 * new IconLayer({
 *  iconAtlas: spritesImage,
 *  iconMapping: spritesMapping,
 *  getIcon: (d) => {
 *   const iconState = d.id === activeMarkerId ? 'active' : 'default';
 *   return getStaticSpritesIcon(spatialLayerKey, iconState, d);
 *  }
 * })
 *
 */
import spritesMapping from '../../assets/static-layers/sprites-output/sprites-info.json';
import spritesImage from '../../assets/static-layers/sprites-output/sprites_2x.png';

const SPATIAL_LAYER_KEYS = {
  EagleEye: 'eagle_eye',
  GeoComm: 'geocomm',
};

// Map spatial layer keys to sprite keys
const sprites = {
  default: {
    default: 'ssl_default',
    active: 'ssl_active',
  },
  [SPATIAL_LAYER_KEYS.EagleEye]: {
    default: 'eagle_eye_default',
    active: 'eagle_eye_active',
  },
  [SPATIAL_LAYER_KEYS.GeoComm]: {
    default: 'geocomm_indoor_map_default',
    active: 'geocomm_indoor_map_active',
  },
};

/**
 * Improve app resilience by matching spatial layer keys that resemble a layer.
 *
 * This sometimes happens when the Implementation Team breaks naming conventions
 * across stacks. For example, we expect `rollkall` but the layer is being
 * prefixed with the stack, e.g: `rollkall_sandbox`, `rollkall_staging`, etc.
 *
 * The Implementation Team is aware that a naming convention must be used going
 * forward, but layers like this exist in production and we need to support them.
 *
 * @param {*}
 * @param {*} targetLayerKey
 * @returns
 */
export const matchIdenticalLayerKey = targetLayerKey => {
  const result = Object.values(SPATIAL_LAYER_KEYS).find(key => {
    return targetLayerKey.toLowerCase().includes(key.toLowerCase());
  });

  if (!result) {
    return targetLayerKey;
  }

  return result;
};

/**
 * Get the sprite icon key for a spatial layer key.
 * @param {string} spatialLayerKey
 * @param {string} iconState - default or active
 * @param {Object} spatialObject
 * @returns
 */
const getStaticSpritesIcon = (spatialLayerKey, iconState = 'default') => {
  const matchedLayerKey = matchIdenticalLayerKey(spatialLayerKey);
  const spriteStates = sprites[matchedLayerKey];

  // Return sprites.default when no sprite is found for the spatial layer key.
  // This can happen in early states of a partner being onboarded where the
  // Implementation Team is provisioning their spatial layer and testing it out
  // before we've updated the code with their icons.
  if (!spriteStates) {
    return sprites.default[iconState];
  }

  return spriteStates[iconState];
};

export { spritesImage, spritesMapping, getStaticSpritesIcon };
export default getStaticSpritesIcon;
